/**
 * FelixVideoSteps — interactive behavior for server-rendered markup.
 */
class FelixVideoSteps extends HTMLElement {
	#steps = []
	#videos = []
	#activeIndex = -1
	#observer = null
	#hasHydratedVideos = false
	#switchToken = 0
	#isInViewport = false
	#progressRaf = 0
	#progressVideo = null
	#progressStep = null

	connectedCallback() {
		const panel = this.querySelector('.video-steps__video-panel')
		this.#steps = [...this.querySelectorAll('.video-steps__step-btn')]
		const videosInSteps = [...this.querySelectorAll('.video-steps__step .video-steps__video-item')]

		if (!panel) {
			return
		}

		videosInSteps.forEach((videoItem) => {
			panel.appendChild(videoItem)
		})

		this.#videos = [...panel.querySelectorAll('.video-steps__video-item')]
		panel.classList.add('video-steps__video-panel--enhanced')

		if (!this.#steps.length || !this.#videos.length) {
			return
		}

		this.#steps.forEach((btn, i) => {
			btn.dataset.stepIndex = i
			btn.addEventListener('click', () => this.#setActive(i))
		})

		this.#videos.forEach((videoItem, i) => {
			videoItem.dataset.videoIndex = i
			videoItem.classList.remove('video-steps__video-item--active')
			const video = videoItem.querySelector('video')
			if (video) {
				video.addEventListener('ended', () => {
					this.#setActive((i + 1) % this.#steps.length)
				})
			}
		})

		// Ensure an active video exists before IntersectionObserver callbacks run.
		this.#setActive(0, false, false)

		this.#observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					this.#isInViewport = entry.isIntersecting
					if (entry.isIntersecting) {
						this.#hydrateAllVideos()
						const video = this.#getActiveVideo()
						if (!video) return
						this.#playVideo(video)
					} else {
						const video = this.#getActiveVideo()
						if (!video) return
						video.pause()
					}
				})
			},
			// rootMargin extends roughly a full viewport before/after so the
			// active video has time to buffer before the section actually
			// reaches the viewport — eliminates the empty-panel gap.
			{ threshold: 0.15, rootMargin: '100% 0px' }
		)
		this.#observer.observe(this)
	}

	disconnectedCallback() {
		this.#observer?.disconnect()
		this.#cancelProgressLoop()
	}

	#setStepState(index) {
		this.#steps.forEach((btn, i) => {
			const isActive = i === index
			const step = btn.closest('.video-steps__step')
			step?.classList.toggle('video-steps__step--active', isActive)
			btn.classList.toggle('video-steps__step--active', isActive)
			// Inactive steps reset their progress fill to 0 (CSS handles the
			// wipe-back transition). The active step's progress is driven by
			// the rAF loop in #startProgressLoop.
			if (step && !isActive) {
				step.style.setProperty('--progress', '0%')
			}
		})
	}

	#bindProgress(index) {
		// Stop the previous rAF loop and switch to the new active video/step.
		this.#cancelProgressLoop()
		const video = this.#videos[index]?.querySelector('video') ?? null
		const step = this.#steps[index]?.closest('.video-steps__step') ?? null
		if (!video || !step) return
		this.#progressVideo = video
		this.#progressStep = step
		this.#startProgressLoop()
	}

	#startProgressLoop() {
		if (this.#progressRaf) return
		const tick = () => {
			const video = this.#progressVideo
			const step = this.#progressStep
			if (!video || !step) {
				this.#progressRaf = 0
				return
			}
			const duration = video.duration
			if (Number.isFinite(duration) && duration > 0) {
				const pct = Math.min(100, Math.max(0, (video.currentTime / duration) * 100))
				step.style.setProperty('--progress', `${pct}%`)
			}
			this.#progressRaf = requestAnimationFrame(tick)
		}
		this.#progressRaf = requestAnimationFrame(tick)
	}

	#cancelProgressLoop() {
		if (this.#progressRaf) {
			cancelAnimationFrame(this.#progressRaf)
			this.#progressRaf = 0
		}
		this.#progressVideo = null
		this.#progressStep = null
	}

	#setVideoState(index) {
		this.#videos.forEach((item, i) => {
			const isActive = i === index
			item.classList.toggle('video-steps__video-item--active', isActive)
		})
	}

	#setActive(index, playImmediately = true, waitForReady = true) {
		if (index === this.#activeIndex) {
			this.#setStepState(index)
			if (playImmediately) {
				const current = this.#getActiveVideo()
				current?.play().catch(() => {})
			}
			return
		}

		const nextVideo = this.#videos[index]?.querySelector('video') ?? null
		const previousIndex = this.#activeIndex
		const previousVideo = this.#videos[previousIndex]?.querySelector('video') ?? null
		const token = ++this.#switchToken

		this.#setStepState(index)

		const finalizeSwitch = () => {
			if (token !== this.#switchToken) {
				return
			}

			if (previousVideo && previousVideo !== nextVideo) {
				previousVideo.pause()
				previousVideo.currentTime = 0
			}

			this.#setVideoState(index)
			this.#activeIndex = index
			this.#bindProgress(index)

			// Upgrade the new active video to full preload so it buffers
			// ahead. Demote the previous one to metadata so it can keep
			// its first-frame preview without using bandwidth.
			if (nextVideo && nextVideo.preload !== 'auto') {
				nextVideo.preload = 'auto'
				nextVideo.setAttribute('preload', 'auto')
			}
			if (previousVideo && previousVideo !== nextVideo && previousVideo.preload === 'auto') {
				previousVideo.preload = 'metadata'
				previousVideo.setAttribute('preload', 'metadata')
			}

			if (playImmediately && nextVideo) {
				nextVideo.currentTime = 0
				if (this.#isInViewport) {
					this.#playVideo(nextVideo)
				}
			}
		}

		if (!nextVideo) {
			finalizeSwitch()
			return
		}

		if (!waitForReady) {
			finalizeSwitch()
			return
		}

		const isReady = nextVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
		if (isReady) {
			finalizeSwitch()
			return
		}

		const onReady = () => {
			cleanup()
			finalizeSwitch()
		}

		const onError = () => {
			cleanup()
			finalizeSwitch()
		}

		const cleanup = () => {
			nextVideo.removeEventListener('loadeddata', onReady)
			nextVideo.removeEventListener('canplay', onReady)
			nextVideo.removeEventListener('error', onError)
		}

		nextVideo.addEventListener('loadeddata', onReady, { once: true })
		nextVideo.addEventListener('canplay', onReady, { once: true })
		nextVideo.addEventListener('error', onError, { once: true })

		if (nextVideo.preload === 'none' || nextVideo.readyState === HTMLMediaElement.HAVE_NOTHING) {
			nextVideo.load()
		}

		if (playImmediately) {
			// No-op: playback starts after ready-state switch to avoid poster flashes.
		}
	}

	#getActiveVideo() {
		return this.#videos[this.#activeIndex]?.querySelector('video') ?? null
	}

	#playVideo(video) {
		if (!video) return

		// Reinforce mobile autoplay requirements before attempting playback.
		video.muted = true
		video.defaultMuted = true
		video.playsInline = true
		video.setAttribute('muted', '')
		video.setAttribute('playsinline', '')

		const attemptPlay = (finalAttempt = false) => {
			const playPromise = video.play()
			if (!playPromise || typeof playPromise.catch !== 'function') {
				return
			}

			playPromise.catch(() => {
				if (!finalAttempt) {
					// Safari may reject initial play before media is actually ready.
					const retry = () => {
						video.removeEventListener('loadeddata', retry)
						video.removeEventListener('canplay', retry)
						attemptPlay(true)
					}
					video.addEventListener('loadeddata', retry, { once: true })
					video.addEventListener('canplay', retry, { once: true })

					if (video.readyState === HTMLMediaElement.HAVE_NOTHING) {
						video.load()
					}
					return
				}

				// Some mobile/network-power modes block autoplay; show controls as fallback.
				video.controls = true
				video.setAttribute('controls', 'controls')
			})
		}

		attemptPlay(false)
	}

	#hydrateAllVideos() {
		if (this.#hasHydratedVideos) {
			return
		}

		this.#videos.forEach((item, i) => {
			const video = item.querySelector('video[data-felix-video-lazy="true"]')
			if (!video) {
				return
			}

			if (video.dataset.felixVideoLoaded === 'true') {
				return
			}

			const sources = video.querySelectorAll('source[data-src]')
			sources.forEach((source) => {
				const src = source.getAttribute('data-src')
				if (!src) {
					return
				}
				source.setAttribute('src', src)
				source.removeAttribute('data-src')
			})

			video.dataset.felixVideoLoaded = 'true'
			video.muted = true
			video.defaultMuted = true
			video.playsInline = true
			video.setAttribute('muted', '')
			video.setAttribute('playsinline', '')

			// Stagger by preload value: the currently-active video buffers
			// fully (preload=auto) so playback is instant; queued videos
			// only fetch metadata + first frame (preload=metadata) so they
			// can render a static preview when activated without competing
			// for bandwidth with the active stream.
			const isActive = i === this.#activeIndex
			const preload = isActive ? 'auto' : 'metadata'
			video.preload = preload
			video.setAttribute('preload', preload)

			video.load()
		})

		this.#hasHydratedVideos = true
	}
}

if (!customElements.get('felix-video-steps')) {
	customElements.define('felix-video-steps', FelixVideoSteps)
}
